/**
 * Annuaire — liste de tous les membres avec filtres avancés et vues grille/liste.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import useMembers from "../hooks/useMembers";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import useInscriptionsPending from "../hooks/useInscriptionsPending";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Avatar from "../components/Avatar";
import Button from "../components/Button";
import Input from "../components/Input";
import MemberForm from "../components/MemberForm";
import ImportMembersModal from "../components/ImportMembersModal";
import ExportMenu from "../components/ExportMenu";
import { inscriptionsApi, presencesApi } from "../services/api";

export default function Annuaire() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const {
    members, filtered, search, setSearch, filters, setFilters,
    eglises, departements, loading, error, deleteMember, addMember, updateMember,
  } = useMembers();
  const safeMembers = members || [];
  const safeFiltered = filtered || [];
  const safeEglises = eglises || [];
  const safeDepartements = departements || [];
  const [view, setView] = useState("grid");
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  // Tabs : "actifs" | "en-attente"
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "en-attente" ? "en-attente" : "actifs");
  const pending = useInscriptionsPending() || { items: [], count: 0, loading: false, refresh: () => {} };

  const handleDelete = async (member) => {
    if (!window.confirm(`Supprimer le membre ${member.full_name || `${member.prenom} ${member.nom}`} ?`)) return;
    const r = await deleteMember(member.id);
    if (r.ok) {
      toast.success("Membre supprimé");
      setSelected(null);
    } else {
      toast.error(r.error);
    }
  };

  /** Soumet le formulaire — création OU édition selon le contexte. */
  const handleSubmit = async (data) => {
    const r = editing
      ? await updateMember(editing.id, data)
      : await addMember(data);
    if (r.ok) {
      toast.success(editing ? "Membre mis à jour" : `${data.prenom} ${data.nom} ajouté à l'annuaire`);
      setFormOpen(false);
      setEditing(null);
      setSelected(null);
    } else {
      toast.error(r.error || "Erreur");
    }
    return r;
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (member) => {
    setEditing(member);
    setFormOpen(true);
    setSelected(null);
  };

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      {/* Header */}
      <header style={{ marginBottom: "24px", display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
            Annuaire
          </h1>
          <p style={{ color: colors.textMuted, fontSize: "14px" }}>
            {safeFiltered.length} membre{safeFiltered.length > 1 ? "s" : ""} {search && `trouvé${safeFiltered.length > 1 ? "s" : ""}`}
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button onClick={openCreate}>+ Nouveau membre</Button>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>📥 Importer</Button>
            <ExportMenu
              filters={{
                eglise_id: filters.eglise_id,
                statut: filters.statut,
              }}
            />
          </div>
        )}
      </header>

      {/* Onglets : Membres actifs / En attente (visible si admin) */}
      {isAdmin && (
        <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: `1px solid ${colors.border}` }}>
          <TabButton
            label="Membres actifs"
            active={tab === "actifs"}
            onClick={() => { setTab("actifs"); setSearchParams({}, { replace: true }); }}
          />
          <TabButton
            label="En attente"
            badge={pending?.count || 0}
            active={tab === "en-attente"}
            onClick={() => { setTab("en-attente"); setSearchParams({ tab: "en-attente" }, { replace: true }); }}
          />
        </div>
      )}

      {tab === "en-attente" ? (
        <EnAttentePanel pending={pending} toast={toast} />
      ) : (
      <>
      {/* Filtres */}
      <Card style={{ marginBottom: "20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", alignItems: "end" }}>
          <Input
            label="Recherche"
            placeholder="Nom, prénom, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon="🔍"
          />
          <Select
            label="Statut"
            value={filters.statut}
            onChange={(v) => setFilters({ ...filters, statut: v })}
            options={[
              { value: "all", label: "Tous" },
              { value: "actif", label: "Actif" },
              { value: "nouveau", label: "Nouveau" },
              { value: "inactif", label: "Inactif" },
              { value: "visiteur", label: "Visiteur" },
            ]}
          />
          <Select
            label="Église"
            value={filters.eglise_id}
            onChange={(v) => setFilters({ ...filters, eglise_id: v })}
            options={[
              { value: "all", label: "Toutes" },
              ...safeEglises.map((e) => ({ value: String(e.id), label: e.nom })),
            ]}
          />
          <Select
            label="Département"
            value={filters.departement}
            onChange={(v) => setFilters({ ...filters, departement: v })}
            options={[
              { value: "all", label: "Tous" },
              ...safeDepartements.map((d) => ({ value: d, label: d })),
            ]}
          />
          <Select
            label="Genre"
            value={filters.genre}
            onChange={(v) => setFilters({ ...filters, genre: v })}
            options={[
              { value: "all", label: "Tous" },
              { value: "homme", label: "Homme" },
              { value: "femme", label: "Femme" },
            ]}
          />
        </div>

        {/* Toggle vue */}
        <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
          <Button variant={view === "grid" ? "primary" : "ghost"} size="sm" onClick={() => setView("grid")}>
            ⊞ Grille
          </Button>
          <Button variant={view === "list" ? "primary" : "ghost"} size="sm" onClick={() => setView("list")}>
            ☰ Liste
          </Button>
        </div>
      </Card>

      {/* Liste */}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Chargement...</div>
      ) : error ? (
        <Card style={{ textAlign: "center", padding: "60px", borderColor: colors.rose }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>⚠️</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Erreur de chargement</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>{error}</p>
        </Card>
      ) : safeFiltered.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>{safeMembers.length === 0 ? "👥" : "🔍"}</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>
            {safeMembers.length === 0 ? "Aucun membre pour l'instant" : "Aucun résultat"}
          </h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            {safeMembers.length === 0
              ? "Commencez par ajouter votre premier membre."
              : "Essayez d'élargir vos filtres."}
          </p>
          {isAdmin && safeMembers.length === 0 && (
            <div style={{ marginTop: "16px" }}>
              <Button onClick={openCreate}>+ Ajouter le premier membre</Button>
            </div>
          )}
        </Card>
      ) : view === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" }}>
          {safeFiltered.map((m) => (
            <MemberCard key={m.id} member={m} onClick={() => setSelected(m)} />
          ))}
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                {["Membre", "Église", "Département", "Statut", "Téléphone", ""].map((h, i) => (
                  <th key={i} style={{ padding: "14px 16px", textAlign: "left", fontSize: "12px", fontWeight: 600, color: colors.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safeFiltered.map((m) => (
                <tr key={m.id} style={{ borderBottom: `1px solid ${colors.borderLight}`, cursor: "pointer", transition: "background 150ms" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    onClick={() => setSelected(m)}>
                  <td style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <Avatar name={m.full_name || `${m.prenom} ${m.nom}`} size="sm" />
                    <div>
                      <div style={{ fontWeight: 600 }}>{m.full_name || `${m.prenom} ${m.nom}`}</div>
                      <div style={{ fontSize: "12px", color: colors.textMuted }}>{m.email || "—"}</div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", color: colors.textMuted, fontSize: "14px" }}>
                    {safeEglises.find((e) => e.id === m.eglise_id)?.nom || "—"}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "14px" }}>{m.departement || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><Badge statut={m.statut}>{m.statut}</Badge></td>
                  <td style={{ padding: "12px 16px", color: colors.textMuted, fontSize: "13px" }}>{m.telephone || "—"}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      </>
      )}

      {/* Modal détail */}
      {selected && (
        <MemberModal
          member={selected}
          eglise={safeEglises.find((e) => e.id === selected.eglise_id)}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onEdit={() => openEdit(selected)}
          onDelete={() => handleDelete(selected)}
        />
      )}

      {/* Formulaire création / édition */}
      <MemberForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={handleSubmit}
        eglises={safeEglises}
        initial={editing}
      />

      {/* Import en masse */}
      <ImportMembersModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </div>
  );
}

/** Mini-composant Select stylisé. */
function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <span style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: "10px",
          padding: "12px 14px",
          fontSize: "14px",
          outline: "none",
          cursor: "pointer",
          fontFamily: fonts.body,
        }}
      >
        {(options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Carte membre (vue grille). */
function MemberCard({ member, onClick }) {
  return (
    <Card hover onClick={onClick}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "12px" }}>
        <Avatar name={member.full_name || `${member.prenom} ${member.nom}`} size="xl" bordered />
        <div>
          <div style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600 }}>
            {member.full_name || `${member.prenom} ${member.nom}`}
          </div>
          {member.role_eglise && (
            <div style={{ color: colors.gold, fontSize: "13px", fontWeight: 500, marginTop: "2px" }}>
              {member.role_eglise}
            </div>
          )}
          {member.profession && (
            <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
              {member.profession}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
          <Badge statut={member.statut}>{member.statut}</Badge>
          {member.departement && <Badge variant="gold" size="sm">{member.departement}</Badge>}
        </div>
        <div style={{ fontSize: "12px", color: colors.textMuted, marginTop: "4px" }}>
          📍 {member.ville}{member.pays ? `, ${member.pays}` : ""}
        </div>
      </div>
    </Card>
  );
}

/** Modale détail membre (Profil + Engagement + Finances). */
function MemberModal({ member, eglise, isAdmin, onClose, onEdit, onDelete }) {
  const [tab, setTab] = useState("profil");
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 400, padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: "16px",
          width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <div style={{ padding: "24px", borderBottom: `1px solid ${colors.border}`, display: "flex", gap: "16px", alignItems: "center" }}>
          <Avatar name={member.full_name || `${member.prenom} ${member.nom}`} size="2xl" bordered />
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700 }}>
              {member.full_name || `${member.prenom} ${member.nom}`}
            </h2>
            {member.role_eglise && <div style={{ color: colors.gold, fontWeight: 500 }}>{member.role_eglise}</div>}
            <div style={{ marginTop: "8px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
              <Badge statut={member.statut}>{member.statut}</Badge>
              {member.departement && <Badge variant="gold">{member.departement}</Badge>}
            </div>
          </div>
          <button onClick={onClose} style={{ color: colors.textMuted, fontSize: "24px", padding: "4px 10px" }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "4px", padding: "12px 24px", borderBottom: `1px solid ${colors.border}`, flexWrap: "wrap" }}>
          {[
            { id: "profil", label: "Profil" },
            { id: "engagement", label: "Engagement" },
            { id: "presences", label: "Présences" },
            { id: "finances", label: "Finances" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "8px 14px", borderRadius: "8px", fontSize: "13px",
                fontWeight: 600, color: tab === t.id ? colors.gold : colors.muted,
                background: tab === t.id ? colors.goldDim : "transparent",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: "24px" }}>
          {tab === "profil" && (
            <Grid>
              <Field label="Email" value={member.email} />
              <Field label="Téléphone" value={member.telephone} />
              <Field label="Adresse" value={member.adresse} />
              <Field label="Ville" value={member.ville} />
              <Field label="Pays" value={member.pays} />
              <Field label="Genre" value={member.genre} />
              <Field label="Date de naissance" value={member.date_naissance} />
              <Field label="Profession" value={member.profession} />
              <Field label="Situation familiale" value={member.situation_familiale} />
            </Grid>
          )}
          {tab === "engagement" && (
            <Grid>
              <Field label="Église" value={eglise?.nom} />
              <Field label="Département" value={member.departement} />
              <Field label="Rôle" value={member.role_eglise} />
              <Field label="Cellule" value={member.cellule_nom} />
              <Field label="Date d'arrivée" value={member.date_arrivee_eglise} />
              <Field label="Date de baptême" value={member.date_bapteme} />
            </Grid>
          )}
          {tab === "presences" && (
            <PresenceSection memberId={member.id} />
          )}
          {tab === "finances" && (
            <div>
              <Card style={{ background: colors.goldDim, border: `1px solid ${colors.goldBorder}`, marginBottom: "12px" }}>
                <div style={{ fontSize: "13px", color: colors.textMuted, marginBottom: "4px" }}>Total des dons</div>
                <div style={{ fontFamily: fonts.heading, fontSize: "32px", fontWeight: 700, color: colors.gold }}>
                  {(member.total_dons || 0).toLocaleString("fr-FR")} €
                </div>
              </Card>
              <p style={{ color: colors.textMuted, fontSize: "14px" }}>
                Détail des contributions disponible dans la page Finances.
              </p>
            </div>
          )}
        </div>

        {isAdmin && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${colors.border}`, display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={onClose}>Fermer</Button>
            <Button variant="danger" onClick={onDelete}>Supprimer</Button>
            <Button onClick={onEdit}>Modifier</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Grid({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: "12px", color: colors.textMuted, marginBottom: "4px", letterSpacing: "0.03em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: "14px", color: colors.text, fontWeight: 500 }}>
        {value || <span style={{ color: colors.textMutedDark }}>—</span>}
      </div>
    </div>
  );
}

/** Section "Présences" : stats + mini-graphique 12 semaines pour un membre. */
function PresenceSection({ memberId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    presencesApi.membreStats(memberId)
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || "Erreur"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [memberId]);

  if (loading) return <div style={{ color: colors.textMuted }}>Chargement...</div>;
  if (error)   return <div style={{ color: colors.rose }}>{error}</div>;
  if (!stats)  return null;

  const niveau = stats.niveau_alerte;
  const accentMap = { rouge: colors.rose, orange: "#FB923C", jaune: colors.gold };
  const accent = niveau ? accentMap[niveau] : colors.green;

  let statusLabel;
  if (stats.derniere_presence === null) {
    statusLabel = "Données insuffisantes";
  } else if (niveau === "rouge") {
    statusLabel = `Absent depuis + 1 mois`;
  } else if (niveau === "orange") {
    statusLabel = `Absent depuis 3 semaines`;
  } else if (niveau === "jaune") {
    statusLabel = `Absent depuis 2 semaines`;
  } else {
    statusLabel = "Assidu";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Grid>
        <Field
          label="Dernière présence"
          value={stats.derniere_presence
            ? new Date(stats.derniere_presence).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
            : null}
        />
        <Field label="Taux (3 mois)" value={stats.total_cultes ? `${stats.taux}%` : null} />
        <div>
          <div style={{ fontSize: "12px", color: colors.textMuted, marginBottom: "4px", letterSpacing: "0.03em", textTransform: "uppercase" }}>
            Statut
          </div>
          <Badge variant={niveau === "rouge" ? "danger" : niveau === "orange" ? "warning" : niveau === "jaune" ? "gold" : "success"} dot>
            {niveau ? "⚠ " : "✓ "}{statusLabel}
          </Badge>
        </div>
      </Grid>

      {/* Mini-graphique 12 semaines */}
      <div>
        <div style={{ fontSize: "12px", color: colors.textMuted, marginBottom: "8px", letterSpacing: "0.03em", textTransform: "uppercase" }}>
          Historique 12 dernières semaines
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "flex-end" }}>
          {(stats.historique_12_semaines || []).map((w, i) => {
            const bg = w.present === true ? colors.green
                      : w.present === false ? colors.rose
                      : colors.border;
            return (
              <div
                key={i}
                title={`${w.label} — ${w.present === true ? "Présent" : w.present === false ? "Absent" : "Pas de donnée"} (semaine du ${new Date(w.date_debut).toLocaleDateString("fr-FR")})`}
                style={{
                  flex: 1,
                  height: 28,
                  background: bg,
                  borderRadius: "4px",
                  opacity: w.present === null ? 0.35 : 1,
                  cursor: "help",
                }}
              />
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "10px", color: colors.textMuted }}>
          <span>S-11</span>
          <span>maintenant</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", color: colors.textMuted, fontSize: "12px" }}>
        <Legend color={colors.green} label="Présent" />
        <Legend color={colors.rose} label="Absent" />
        <Legend color={colors.border} label="Pas de donnée" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <span style={{ display: "inline-block", width: 10, height: 10, background: color, borderRadius: 2 }} />
      {label}
    </span>
  );
}

function TabButton({ label, badge, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "10px 16px",
        borderBottom: active ? `2px solid ${colors.gold}` : "2px solid transparent",
        color: active ? colors.gold : colors.textMuted,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        background: "transparent",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        transition: "color 150ms, border-color 150ms",
      }}
    >
      {label}
      {badge ? (
        <span style={{
          background: colors.rose,
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 999,
        }}>
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function EnAttentePanel({ pending, toast }) {
  const [busyId, setBusyId] = useState(null);
  const items = pending?.items || [];

  const handleValider = async (item) => {
    setBusyId(item.id);
    try {
      await inscriptionsApi.valider(item.id);
      toast.success(`✓ ${item.full_name} accepté`);
      pending?.refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusyId(null); }
  };

  const handleRefuser = async (item) => {
    if (!window.confirm(`Refuser ${item.full_name} ?\n\nSon compte sera désactivé.`)) return;
    setBusyId(item.id);
    try {
      await inscriptionsApi.refuser(item.id);
      toast.success("Inscription refusée");
      pending?.refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusyId(null); }
  };

  if (pending?.loading) {
    return <div style={{ color: colors.textMuted }}>Chargement…</div>;
  }
  if (items.length === 0) {
    return (
      <Card style={{ textAlign: "center", padding: 60 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
        <h3 style={{ fontFamily: fonts.heading, fontSize: 22 }}>Aucun inscrit en attente</h3>
        <p style={{ color: colors.textMuted, marginTop: 8 }}>
          Les nouveaux fidèles qui s'inscriront avec votre code apparaîtront ici.
        </p>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
            {["Fidèle", "Email", "Inscrit", "Église", "Actions"].map((h, i) => (
              <th key={i} style={{ padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: colors.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
              <td style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar name={item.full_name} size="sm" />
                <span style={{ fontWeight: 600 }}>{item.full_name}</span>
              </td>
              <td style={{ padding: "12px 16px", color: colors.textMuted, fontSize: 13 }}>{item.email}</td>
              <td style={{ padding: "12px 16px", color: colors.textMuted, fontSize: 13 }}>
                {item.created_at ? new Date(item.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
              </td>
              <td style={{ padding: "12px 16px", fontSize: 13 }}>{item.eglise_nom || "—"}</td>
              <td style={{ padding: "12px 16px", textAlign: "right" }}>
                <div style={{ display: "inline-flex", gap: 6 }}>
                  <Button size="sm" onClick={() => handleValider(item)} disabled={busyId === item.id}>
                    ✓ Accepter
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleRefuser(item)} disabled={busyId === item.id}>
                    ✗ Refuser
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
